import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Download,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  School,
  CalendarMonth,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import { useStudentResults } from "../../hooks/queries/useExams";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const ExamResults = () => {
  const { user } = useSelector((state) => state.auth);
  const [selectedExam, setSelectedExam] = useState("midterm");
  const [selectedSemester, setSelectedSemester] = useState("current");
  const { data: resultsData, isLoading: loading } = useStudentResults(
    user?.id,
    { exam: selectedExam, semester: selectedSemester }
  );
  const results = resultsData?.results || resultsData;

  // Sample data for demo
  const sampleResults = {
    exam: "Mid-Term Examination",
    semester: "Semester 1, 2025-26",
    date: "October 2025",
    totalMarks: 500,
    obtainedMarks: 425,
    percentage: 85,
    rank: 5,
    totalStudents: 120,
    grade: "A",
    subjects: [
      {
        name: "Mathematics",
        totalMarks: 100,
        obtainedMarks: 92,
        grade: "A+",
        teacher: "Dr. Smith",
      },
      {
        name: "Physics",
        totalMarks: 100,
        obtainedMarks: 85,
        grade: "A",
        teacher: "Prof. Johnson",
      },
      {
        name: "Chemistry",
        totalMarks: 100,
        obtainedMarks: 88,
        grade: "A",
        teacher: "Dr. Wilson",
      },
      {
        name: "English",
        totalMarks: 100,
        obtainedMarks: 78,
        grade: "B+",
        teacher: "Ms. Davis",
      },
      {
        name: "Biology",
        totalMarks: 100,
        obtainedMarks: 82,
        grade: "A",
        teacher: "Dr. Green",
      },
    ],
    previousExams: [
      { name: "Unit Test 1", percentage: 82, rank: 8 },
      { name: "Unit Test 2", percentage: 78, rank: 12 },
      { name: "Mid-Term", percentage: 85, rank: 5 },
    ],
  };

  const displayResults = results || sampleResults;

  const performanceData = {
    labels: displayResults.subjects.map((s) => s.name),
    datasets: [
      {
        data: displayResults.subjects.map((s) => s.obtainedMarks),
        backgroundColor: [
          "#1976d2",
          "#9c27b0",
          "#f44336",
          "#ff9800",
          "#4caf50",
        ],
        borderWidth: 0,
      },
    ],
  };

  const trendData = {
    labels: displayResults.previousExams.map((e) => e.name),
    datasets: [
      {
        label: "Percentage",
        data: displayResults.previousExams.map((e) => e.percentage),
        borderColor: "#1976d2",
        backgroundColor: "rgba(25, 118, 210, 0.1)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case "A+":
        return "success";
      case "A":
        return "success";
      case "B+":
        return "primary";
      case "B":
        return "primary";
      case "C+":
        return "warning";
      case "C":
        return "warning";
      default:
        return "error";
    }
  };

  const handleDownload = () => {
    console.log("Downloading result...");
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Examination Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your academic performance and grades
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownload}
        >
          Download Report Card
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Examination</InputLabel>
              <Select
                value={selectedExam}
                label="Examination"
                onChange={(e) => setSelectedExam(e.target.value)}
              >
                <MenuItem value="unit1">Unit Test 1</MenuItem>
                <MenuItem value="unit2">Unit Test 2</MenuItem>
                <MenuItem value="midterm">Mid-Term Examination</MenuItem>
                <MenuItem value="final">Final Examination</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Semester</InputLabel>
              <Select
                value={selectedSemester}
                label="Semester"
                onChange={(e) => setSelectedSemester(e.target.value)}
              >
                <MenuItem value="current">Current Semester</MenuItem>
                <MenuItem value="previous">Previous Semester</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Overall Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: "primary.main", color: "white" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h3" fontWeight={700}>
                {displayResults.percentage}%
              </Typography>
              <Typography variant="body2">Overall Percentage</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "success.light",
                  mx: "auto",
                  mb: 1,
                  width: 48,
                  height: 48,
                }}
              >
                <EmojiEvents color="success" />
              </Avatar>
              <Typography variant="h4" fontWeight={700} color="success.main">
                #{displayResults.rank}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Class Rank
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "primary.light",
                  mx: "auto",
                  mb: 1,
                  width: 48,
                  height: 48,
                }}
              >
                <School color="primary" />
              </Avatar>
              <Typography variant="h4" fontWeight={700}>
                {displayResults.grade}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overall Grade
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight={700} color="primary">
                {displayResults.obtainedMarks}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                out of {displayResults.totalMarks}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={displayResults.percentage}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Subject-wise Results */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Subject-wise Results
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Subject</TableCell>
                      <TableCell>Teacher</TableCell>
                      <TableCell align="center">Marks</TableCell>
                      <TableCell align="center">Percentage</TableCell>
                      <TableCell align="center">Grade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayResults.subjects.map((subject) => (
                      <TableRow key={subject.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {subject.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {subject.teacher}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {subject.obtainedMarks}/{subject.totalMarks}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={
                                (subject.obtainedMarks / subject.totalMarks) *
                                100
                              }
                              sx={{ width: 60, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption">
                              {(
                                (subject.obtainedMarks / subject.totalMarks) *
                                100
                              ).toFixed(0)}
                              %
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={subject.grade}
                            size="small"
                            color={getGradeColor(subject.grade)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Marks Distribution
              </Typography>
              <Box
                sx={{
                  height: 250,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Doughnut
                  data={performanceData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: "bottom" } },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Trend */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Performance Trend
          </Typography>
          <Box sx={{ height: 300 }}>
            <Line
              data={trendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: { beginAtZero: true, max: 100 },
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Exam Info */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Examination Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Examination
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {displayResults.exam}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Semester
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {displayResults.semester}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Conducted
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {displayResults.date}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Total Students
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {displayResults.totalStudents}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExamResults;
